import { VStack, Text, HStack } from "@chakra-ui/react";
import {
  UseFormWatch,
  UseFormSetValue,
  useFormContext,
  useWatch,
} from "react-hook-form";
import MediaUploadArea from "../MediaUploadArea";
import { S3Response } from "react-s3-uploader";
import { AdCreativeFormData } from "../../forms/adcreative";
import { ErrorMessage } from "@hookform/error-message";
import { validateAspectRatio } from "src/utils/aspectRatioValidator";
import {
  analyzePlacementRequirements,
  validatePortraitAspectRatio,
  validateSquareAspectRatio,
} from "src/utils/aspectRatio";
import {
  mapFacebookPlacementToAspectKey,
  mapInstagramPlacementToAspectKey,
} from "src/utils/aspectRatio/placementMap";
import { useEffect, useMemo, useState } from "react";
import useMeasure from "react-use-measure";

interface MediaUploadResult {
  cdn_url: string;
  file_key: string;
  temp_url: string;
  mime_type: string;
  file: File;
}

interface MediaUploadConfig {
  format: "square" | "portrait";
  name: string;
  resolution: string;
  ratio: string;
  validationFunction: (
    file: File,
    tolerance?: number
  ) => Promise<{ isValid: boolean; errorMessage?: string }>;
  errorFieldPath: string;
  getUrl: (images: any, videos: any, mediaType: string) => string | undefined;
  setValue: (
    result: MediaUploadResult,
    setValue: UseFormSetValue<AdCreativeFormData>
  ) => void;
}

interface SingleTabProps {
  createPublicUploadUrl: (params: {
    file: File;
    callback: (params: S3Response) => void | Promise<void | string>;
  }) => Promise<S3Response>;
}

const SingleTab = ({ createPublicUploadUrl }: SingleTabProps) => {
  const {
    formState: { errors },
    setError,
    setValue,
    watch,
  } = useFormContext<AdCreativeFormData>();
  const images = watch("images");
  const videos = watch("videos");
  const media_type = watch("media_type");

  const [formats, setFormats] = useState<("square" | "portrait")[]>([
    "square",
    "portrait",
  ]);

  // Determine if we're in video mode by checking if any videos exist
  const isVideoMode = useMemo(() => {
    return !!(
      (videos?.square?.length > 0 && videos.square[0]?.source) ||
      (videos?.portrait?.length > 0 && videos.portrait[0]?.source)
    );
  }, [videos]);

  const mediaUploadConfigs: MediaUploadConfig[] = [
    {
      format: "square" as const,
      name: isVideoMode ? "Square Video" : "Square",
      resolution: "1:1 - 1080px x 1080px",
      ratio: "1/1",
      validationFunction: validateSquareAspectRatio,
      errorFieldPath: "images.square",
      getUrl: (images, videos, mediaType) => {
        // Check if there's a video for this format
        const hasVideo = !!videos?.square?.[0]?.source;
        return hasVideo
          ? videos?.square?.[0]?.source
          : images?.square?.[0]?.picture_url;
      },
      setValue: (result, setValue) => {
        const isVideo = result.mime_type.startsWith("video/");

        if (isVideo) {
          // Clear images when video is uploaded
          setValue("images.square", [], {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
          setValue(
            "videos.square",
            [
              {
                fileKey: result.file_key,
                source: result.cdn_url,
                thumbnail_url: result.cdn_url,
              },
            ],
            {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            }
          );
        } else {
          // Clear videos when image is uploaded
          setValue("videos.square", [], {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
          setValue(
            "images.square",
            [
              {
                fileKey: result.file_key,
                picture_url: result.cdn_url,
              },
            ],
            {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            }
          );
        }
      },
    },
    {
      format: "portrait" as const,
      name: isVideoMode ? "Portrait Video" : "Portrait",
      resolution: "4:5 - 1080px x 1350px",
      ratio: "4/5",
      validationFunction: validatePortraitAspectRatio,
      errorFieldPath: "images.portrait",
      getUrl: (images, videos, mediaType) => {
        // Check if there's a video for this format
        const hasVideo = !!videos?.portrait?.[0]?.source;
        return hasVideo
          ? videos?.portrait?.[0]?.source
          : images?.portrait?.[0]?.picture_url;
      },
      setValue: (result, setValue) => {
        const isVideo = result.mime_type.startsWith("video/");

        if (isVideo) {
          // Clear images when video is uploaded
          setValue("images.portrait", [], {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
          setValue(
            "videos.portrait",
            [
              {
                fileKey: result.file_key,
                source: result.cdn_url,
                thumbnail_url: result.cdn_url,
              },
            ],
            {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            }
          );
        } else {
          // Clear videos when image is uploaded
          setValue("videos.portrait", [], {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
          setValue(
            "images.portrait",
            [
              {
                fileKey: result.file_key,
                picture_url: result.cdn_url,
              },
            ],
            {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            }
          );
        }
      },
    },
  ].filter((config) => formats.includes(config.format));

  const facebookPlacements = watch("facebook_placements") || [];
  const instagramPlacements = watch("instagram_placements") || [];
  const placements = useMemo(
    () => [
      ...facebookPlacements.map(mapFacebookPlacementToAspectKey),
      ...instagramPlacements.map(mapInstagramPlacementToAspectKey),
    ],
    [facebookPlacements.join(","), instagramPlacements.join(",")]
  );

  useEffect(() => {
    const analysis = analyzePlacementRequirements(placements);

    if (!placements.length) {
      setFormats(["square", "portrait"]);
      return;
    }

    setFormats([
      ...(analysis.needsSquare ? ["square"] : []),
      ...(analysis.needsPortrait ? ["portrait"] : []),
    ] as ("square" | "portrait")[]);
  }, [placements]);

  return (
    <VStack
      bg="white"
      rounded="xl"
      alignItems={"center"}
      justifyContent={"center"}
      w="full"
      p={4}
      border="1px solid"
      borderColor="gray.200"
      spacing={4}
    >
      <Text fontWeight="bold" fontSize="sm" w="full" textAlign="center">
        {isVideoMode
          ? "Upload videos for both formats"
          : "Upload photos for both formats"}
      </Text>
      <VStack w="full" spacing={4}>
        {mediaUploadConfigs.map((config) => {
          // Check if there's a video for this specific format
          const hasVideoForFormat = !!(config.format === "square"
            ? videos?.square?.[0]?.source
            : videos?.portrait?.[0]?.source);
          const mediaTypeForFormat = hasVideoForFormat ? "video" : "photo";

          return (
            <VStack key={config.format} w="full" spacing={2}>
              <MediaUploadArea
                name={config.name}
                mediaType={mediaTypeForFormat}
                resolution={config.resolution}
                ratio={config.ratio}
                url={config.getUrl(images, videos, mediaTypeForFormat)}
                width="200px"
                createUploadUrl={createPublicUploadUrl}
                onChange={async (result) => {
                  const isVideo = result.mime_type.startsWith("video/");
                  const errorFieldPath = isVideo
                    ? `videos.${config.format}`
                    : `images.${config.format}`;

                  if (result.file) {
                    const { isValid, errorMessage } =
                      await config.validationFunction(result.file, 0.4);

                    console.log({
                      isValid,
                      errorMessage,
                      errorFieldPath,
                      format: config.format,
                    });

                    if (!isValid) {
                      setError(errorFieldPath as any, {
                        message: errorMessage,
                      });
                      return;
                    }
                  }

                  config.setValue(result, setValue);
                }}
              />
              <ErrorMessage
                name={`images.${config.format}`}
                errors={errors}
                render={({ message }) => (
                  <Text pt={2} fontSize="sm" color="kydRed.200">
                    {message}
                  </Text>
                )}
              />
              <ErrorMessage
                name={`videos.${config.format}`}
                errors={errors}
                render={({ message }) => (
                  <Text pt={2} fontSize="sm" color="kydRed.200">
                    {message}
                  </Text>
                )}
              />
            </VStack>
          );
        })}
      </VStack>
    </VStack>
  );
};

export default SingleTab;
